// All per-task auto-open bookkeeping lives here rather than in component refs so
// that it shares one lifetime with the pending submissions. The workbench subtree
// remounts in normal use (the environment gate in `AgentsWorkbenchHost` and the
// lazy boundary in `AgentsTaskWorkbench`), and rebuilding this state from empty on
// a remount would drop eligibility a dispatch in this page already granted.

// A submission is only ever matched to a turn the dispatch itself named, so a turn
// replayed from history or restored after a reconnect can never claim one.
type PendingSubmission = {
  readonly id: number
  turnId?: string
}

type TaskAutoOpenState = {
  pendingSubmissions: PendingSubmission[]
  // Turns that claimed a submission, and so are allowed to open their artifact.
  claimedTurnIds: Set<string>
  // Turns whose auto-open opportunity is spent, whether it opened or was skipped.
  settledTurnIds: Set<string>
}

const taskAutoOpenStates = new Map<string, TaskAutoOpenState>()
let nextSubmissionId = 1

// Eligibility changes outside React's data flow: a dispatch receipt arrives in a
// promise callback, not in a render. Consumers read this version so binding one is
// observed even when no relay event follows it. Claiming deliberately does not
// bump it, because a claim is the consumer's own work.
let eligibilityVersion = 0
const eligibilityListeners = new Set<() => void>()

function publishEligibilityChange(): void {
  eligibilityVersion++
  for (const listener of eligibilityListeners) listener()
}

export function subscribeToAutoOpenEligibility(listener: () => void): () => void {
  eligibilityListeners.add(listener)
  return () => {
    eligibilityListeners.delete(listener)
  }
}

export function getAutoOpenEligibilityVersion(): number {
  return eligibilityVersion
}

export function getTaskAutoOpenState(taskId: string): TaskAutoOpenState {
  let state = taskAutoOpenStates.get(taskId)
  if (!state) {
    state = {pendingSubmissions: [], claimedTurnIds: new Set(), settledTurnIds: new Set()}
    taskAutoOpenStates.set(taskId, state)
  }
  return state
}

// One record per pending submission, not one flag per task: the composer lets the
// user steer and then queue while a turn is still active, so a task can have
// several dispatches in flight and each one must be able to open the artifact its
// own turn produces.
export function markUserStartedTurn(taskId: string): number {
  const id = nextSubmissionId++
  getTaskAutoOpenState(taskId).pendingSubmissions.push({id, turnId: undefined})
  publishEligibilityChange()
  return id
}

// The dispatch receipt decides whether this submission can ever open an artifact.
// A receipt naming a turn binds to it. A receipt that only names a queue entry
// never will, because the turn that eventually runs it is not identified here, so
// the submission retires immediately rather than waiting for a turn to guess at.
export function bindUserStartedTurn(taskId: string, submissionId: number, receipt: {turnId?: string}): void {
  if (receipt.turnId === undefined) {
    unmarkUserStartedTurn(taskId, submissionId)
    return
  }
  const submission = taskAutoOpenStates.get(taskId)?.pendingSubmissions.find(entry => entry.id === submissionId)
  if (!submission) return
  submission.turnId = receipt.turnId
  publishEligibilityChange()
}

function removeSubmission(taskId: string, predicate: (submission: PendingSubmission) => boolean): boolean {
  const state = taskAutoOpenStates.get(taskId)
  if (!state) return false
  const index = state.pendingSubmissions.findIndex(predicate)
  if (index === -1) return false
  state.pendingSubmissions.splice(index, 1)
  return true
}

// The submission is armed before the dispatch mutation runs, so a dispatch that
// never reaches the server has to take it back.
export function unmarkUserStartedTurn(taskId: string, submissionId: number): void {
  if (removeSubmission(taskId, submission => submission.id === submissionId)) publishEligibilityChange()
}

// A turn names its submission outright. This is the only way a turn becomes
// eligible, which is what keeps replayed history and reconnect-restored turns from
// opening anything: they were never dispatched from this page, so nothing names
// them. Removing the submission on the first match is what makes the grant
// single-use across later observations of the same turn.
export function claimTurnById(taskId: string, turnId: string): boolean {
  return removeSubmission(taskId, submission => submission.turnId === turnId)
}

// Task surfaces are keyed by task id, so moving between tasks unmounts the hook
// rather than handing it a new task. Declaring the task being observed is what
// drops the outgoing task's bookkeeping, and it works from any surface because it
// lives with the state instead of with a component. Dropping state can only cost an
// auto-open, never cause one, so surfaces that briefly overlap stay safe.
export function setActiveAutoOpenTask(taskId: string): void {
  for (const key of taskAutoOpenStates.keys()) {
    if (key !== taskId) taskAutoOpenStates.delete(key)
  }
}

// Reads the pending submissions without retiring any, so assertions can observe the
// state they describe instead of mutating it.
export function peekPendingSubmissions(taskId: string): readonly PendingSubmission[] {
  return taskAutoOpenStates.get(taskId)?.pendingSubmissions ?? []
}

export function resetUserStartedTurnsForTests(): void {
  taskAutoOpenStates.clear()
  nextSubmissionId = 1
  eligibilityVersion = 0
  eligibilityListeners.clear()
}
